import { toast } from 'react-hot-toast';
import {} from '@/utils/helpers';
import { DashboardHeader } from './ui/DashboardHeader';
import KeyMetricsGrid from './ui/KeyMetricsGrid';
import { InterestRateTypeCard } from './ui/InterestRateTypeCard';
import { SectorAllocationCard } from './ui/SectorAllocationCard';
import { RiskDistributionCard } from './ui/RiskDistributionCard';
import {
  usePortfolioData,
  usePortfolioMetadata,
} from '@/hooks/usePortfolioData';
import Stratifications from './ui/Stratifications/Stratifications';
import { useDashboardAnalytics } from './hooks/useDashboardAnalytics';

const DashboardPage = () => {
  // Use TanStack Query hooks for data fetching
  const {
    portfolioSummary,
    valuationReports: pricingOutput,
    isLoading,
    isError,
    error,
  } = usePortfolioData();

  const { data: analyticsData, isLoading: isAnalyticsLoading } =
    useDashboardAnalytics();
  const { metadata: pricingMetadata } = usePortfolioMetadata();

  // Handle errors
  if (isError && error) {
    console.error('Error fetching portfolio data:', error);
    toast.error('Failed to load portfolio data');
  }


  // Get interest type distribution from analytics data
  const interestTypeRows = analyticsData?.interest_type_distribution?.rows || [];
  const floatingRow = interestTypeRows.find(
    (row) => row.category === 'Floating'
  );
  const fixedRow = interestTypeRows.find((row) => row.category === 'Fixed');
  
  const floatingPct = floatingRow
    ? parseFloat(floatingRow.pct_of_portfolio)
    : 0;
  const fixedPct = fixedRow ? parseFloat(fixedRow.pct_of_portfolio) : 0;

  if (isLoading || isAnalyticsLoading) {
    return (
      <div
        className="page-container"
        data-test-id="pricing-dashboard-loading-container"
      >
        <div className="page-inner" data-test-id="loading-container">
          <div
            className="flex items-center justify-center h-64"
            data-test-id="loading-spinner"
          >
            <div className="text-lg text-gray-600" data-test-id="loading-text">
              Loading portfolio data...
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="page-container"
      data-test-id="portfolio-dashboard-container"
    >
      <div className="page-inner" data-test-id="portfolio-dashboard-inner">
        {/* Header */}
        <DashboardHeader
          analysisDateText={
            pricingOutput && pricingMetadata
              ? pricingMetadata.analysis_date
                ? new Date(pricingMetadata.analysis_date).toLocaleDateString()
                : 'N/A'
              : 'N/A'
          }
          totalLoansText={pricingOutput ? String(pricingOutput.length) : '0'}
          sourceText={pricingMetadata ? 'Valuation Reports' : 'N/A'}
        />

        {/* Key Metrics Grid */}
        <KeyMetricsGrid data={analyticsData?.portfolio_statistics} />

        {/* Charts and Analysis Grid */}
        <div
          className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8"
          data-test-id="charts-grid"
        >
          {/* Sector Allocation */}
          <SectorAllocationCard
            groups={
              portfolioSummary && portfolioSummary.groupings.length > 0
                ? portfolioSummary.groupings.map((g) => ({
                    name: g.name,
                    percentage: g.percentage,
                  }))
                : []
            }
          />

          {/* Right Column: Risk Distribution and Interest Rate Type */}
          <div className="flex flex-col gap-4">
            {/* Risk Distribution */}
            <RiskDistributionCard
              rows={analyticsData?.loan_status.rows || []}
            />

            {/* Interest Rate Type Box */}
            <InterestRateTypeCard floatingPct={floatingPct} fixedPct={fixedPct} />
          </div>
        </div>

        <Stratifications data={analyticsData || undefined} />
      </div>
    </div>
  );
};

export default DashboardPage;
